/**
 * **C02 · The forum one-pager** (Stream C, Documents.dc.html): for the parish, in plain words —
 * what came in, what it cost, where the rest went — posted in the vestibule and read at the
 * forum. The figures are the forum P&L's (`public_financials`, ruled Oct 8: its five expense
 * lines and its distribution, as Money serves them); the closing line is Volunteers' — the year's
 * people and counted hours, and the families whose commitment stands complete. This lays them out
 * as drawn and computes nothing a figure engine already does.
 */
import { dollars } from './dollars.js';
import { NO_DATA, type PaperCell, type PaperPage } from './paper_page.js';

type Cell = string | number | null;
/** The forum report's sections as Money serves them (`GET /money/reports/forum`). */
export interface ForumSection {
  head: string;
  rows: Cell[][];
  totals?: Cell[];
}

export interface ForumInput {
  fiscalYear: number;
  /** null: the forum report did not answer, or answered NO DATA. */
  sections: ForumSection[] | null;
  /** The year's people who worked and their counted minutes; null when Volunteers did not answer. */
  together: { people: number; minutes: number } | null;
  /** Each family's standing this year; null when Volunteers did not answer. */
  families: { status: string }[] | null;
}

/** `$387,728` — the big line and a head say whole dollars, as drawn. */
export const wholeDollarsOf = (cents: number): string =>
  dollars(Math.round(cents / 100) * 100).replace(/\.00$/, '');
const thousands = (n: number): string => String(n).replace(/\B(?=(\d{3})+(?!\d))/g, ',');
const fig = (c: Cell | undefined): PaperCell => (typeof c === 'number' ? c : NO_DATA);

export function forumPage(i: ForumInput): PaperPage {
  const fy = String(i.fiscalYear);
  const sec = (head: string): ForumSection | undefined =>
    i.sections?.find((s) => s.head === head || s.head.startsWith(`${head} ·`));
  const income = sec('Income')?.totals?.[1];
  const expenses = sec('Expenses');
  const net = sec('Net Festival Proceeds')?.rows.find((r) => r[0] === 'Net Festival Proceeds')?.[1];
  const distribution = sec('Distribution');
  const people = i.together ? thousands(i.together.people) : NO_DATA;
  const hours = i.together ? thousands(Math.round(i.together.minutes / 60)) : NO_DATA;
  const complete = i.families
    ? thousands(i.families.filter((f) => f.status === 'Complete').length)
    : NO_DATA;
  return {
    title: `Apple Fest ${String(i.fiscalYear - 1)} · where the money went`,
    sub: `For the parish · the festival’s year ended June 30, ${fy}`,
    big: typeof income === 'number' ? wholeDollarsOf(income) : NO_DATA,
    bigSub: 'came in — from food, the Pub, the bakery, rides, raffle, bingo and our sponsors',
    blocks: [
      {
        head: 'IT COST',
        cols: '1fr 130px',
        rows: expenses
          ? [
              ...expenses.rows.map((r) => [String(r[0] ?? ''), fig(r[1])]),
              ['TOTAL', fig(expenses.totals?.[1])],
            ]
          : [[NO_DATA, '']],
      },
      {
        head: `WHAT WAS LEFT · ${typeof net === 'number' ? wholeDollarsOf(net) : NO_DATA}`,
        cols: '1fr 130px',
        rows: distribution
          ? distribution.rows.map((r) => [String(r[0] ?? ''), fig(r[1])])
          : [[NO_DATA, '']],
      },
      {
        head: '',
        cols: '1fr',
        rows: [
          [
            `${people} volunteers worked ${hours} hours. ${complete} school families met their commitment. Thank you.`,
          ],
        ],
      },
    ],
    foot: `Forum one-pager · figures from the FY${fy} close`,
  };
}
